import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDistributionType } from "./enums/enumDistributionType.generated";

// Model Objects

export class SellarStockUpdateLocationReadOnly extends ModelObject<SellarStockUpdateLocationReadOnly> {

    /**
    * The sellar link settings which this mapping belongs to.
    */
    public sellarSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sellarSettingsId = Guid.parseOrEmpty(jsonSerializedObject.sellarSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sellarSettingsId = this.sellarSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sellarSettingsId", rule: (v: any) => !v.isEmpty() || "SellarStockUpdateLocationReadOnly.SellarSettingsId cannot be empty"},
    ];
}


export class SellarStockUpdateLocation extends ModelObject<SellarStockUpdateLocation> {

    /**
    * Stock location from which stocks are taken to be pushed to the Sellar.
    */
    public stockLocationId: Guid = Guid.createEmpty();
    public readOnly: SellarStockUpdateLocationReadOnly = new SellarStockUpdateLocationReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.readOnly = new SellarStockUpdateLocationReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "SellarStockUpdateLocation.StockLocationId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class SellarShippingMappingReadOnly extends ModelObject<SellarShippingMappingReadOnly> {

    /**
    * The sellar link settings which this mapping belongs to.
    */
    public sellarSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sellarSettingsId = Guid.parseOrEmpty(jsonSerializedObject.sellarSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sellarSettingsId = this.sellarSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sellarSettingsId", rule: (v: any) => !v.isEmpty() || "SellarShippingMappingReadOnly.SellarSettingsId cannot be empty"},
    ];
}


export class SellarShippingMapping extends ModelObject<SellarShippingMapping> {

    /**
    * Sellar fulfillment type to map to a Brewman distribution.
    * Not Nullable
    */
    public sellarFulfillmentType: string = "";
    /**
    * The Brewman distribution type to use for orders with this shipping method.
    */
    public distributionType: EnumDistributionType = EnumDistributionType.none;
    private _deliveryAreaId: Guid | null = null;
    /**
    * Delivery Area Id, required if the distribution type is delivery.
    */
    public get deliveryAreaId(): Guid | null { return this._deliveryAreaId }
    public set deliveryAreaId(value: Guid | null) {
      this._deliveryAreaId = value === undefined ? null : value;
    }
    private _courierId: Guid | null = null;
    /**
    * Courier Id, required if the distribution type is courier.
    */
    public get courierId(): Guid | null { return this._courierId }
    public set courierId(value: Guid | null) {
      this._courierId = value === undefined ? null : value;
    }
    public readOnly: SellarShippingMappingReadOnly = new SellarShippingMappingReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sellarFulfillmentType = jsonSerializedObject.sellarFulfillmentType;
          this.distributionType = EnumDistributionType.getByValue(jsonSerializedObject.distributionType, this.distributionType);
          this.deliveryAreaId = jsonSerializedObject.deliveryAreaId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveryAreaId);
          this.courierId = jsonSerializedObject.courierId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.courierId);
          this.readOnly = new SellarShippingMappingReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sellarFulfillmentType = this.sellarFulfillmentType;
        toRet.distributionType = this.distributionType.value;
        toRet.deliveryAreaId = this.deliveryAreaId == null ? null : this.deliveryAreaId.toString();
        toRet.courierId = this.courierId == null ? null : this.courierId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "deliveryAreaId", rule: (v: any) => v == null || !v.isEmpty() || "SellarShippingMapping.DeliveryAreaId cannot be empty"},
            {field: "courierId", rule: (v: any) => v == null || !v.isEmpty() || "SellarShippingMapping.CourierId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class SellarSettingsReadOnly extends ModelObject<SellarSettingsReadOnly> {

    private _webhookOrderCreatedId: number | null = null;
    /**
    * The Sellar webhook id if an order.created webhook has been registered for this tenant. This id is needed to unregister.
    */
    public get webhookOrderCreatedId(): number | null { return this._webhookOrderCreatedId }
    public set webhookOrderCreatedId(value: number | null) {
      this._webhookOrderCreatedId = value === undefined ? null : value;
    }
    private _webhookOrderUpdatedId: number | null = null;
    /**
    * The Sellar webhook id if an order.updated webhook has been registered for this tenant. This id is needed to unregister.
    */
    public get webhookOrderUpdatedId(): number | null { return this._webhookOrderUpdatedId }
    public set webhookOrderUpdatedId(value: number | null) {
      this._webhookOrderUpdatedId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.webhookOrderCreatedId = jsonSerializedObject.webhookOrderCreatedId == null ? null : jsonSerializedObject.webhookOrderCreatedId;
          this.webhookOrderUpdatedId = jsonSerializedObject.webhookOrderUpdatedId == null ? null : jsonSerializedObject.webhookOrderUpdatedId;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.webhookOrderCreatedId = this.webhookOrderCreatedId == null ? null : this.webhookOrderCreatedId;
        toRet.webhookOrderUpdatedId = this.webhookOrderUpdatedId == null ? null : this.webhookOrderUpdatedId;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "webhookOrderCreatedId", rule: (v: any) => v == null || v >= -2147483648 || "WebhookOrderCreatedId is not greater than minimum allowed value: -2147483648"},
            {field: "webhookOrderCreatedId", rule: (v: any) => v == null || v <= 2147483647 || "WebhookOrderCreatedId is above maximum allowed value: 2147483647"},
            {field: "webhookOrderUpdatedId", rule: (v: any) => v == null || v >= -2147483648 || "WebhookOrderUpdatedId is not greater than minimum allowed value: -2147483648"},
            {field: "webhookOrderUpdatedId", rule: (v: any) => v == null || v <= 2147483647 || "WebhookOrderUpdatedId is above maximum allowed value: 2147483647"},
    ];
}


export class SellarSettings extends ModelObject<SellarSettings> {

    public id: Guid = Guid.createEmpty();
    /**
    * Sellar's account name.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public accountName: string = "";
    /**
    * The api token to use for access to Sellar's API.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public apiToken: string = "";
    private _deliveryStockItemId: Guid | null = null;
    /**
    * Stock Item Id to be used for delivery charge of imported orders. Delivery charges will be ignored if not defined.
    */
    public get deliveryStockItemId(): Guid | null { return this._deliveryStockItemId }
    public set deliveryStockItemId(value: Guid | null) {
      this._deliveryStockItemId = value === undefined ? null : value;
    }
    private _ignoreBeforeDate: LocalDate | null = null;
    /**
    * If provided then orders on Sellar from before this date will be ignored.
    */
    public get ignoreBeforeDate(): LocalDate | null { return this._ignoreBeforeDate }
    public set ignoreBeforeDate(value: LocalDate | null) {
      this._ignoreBeforeDate = value === undefined ? null : value;
    }
    /**
    * Whether the current integration should run continuously in the background.
    */
    public runInBackground: boolean = false;
    /**
    * Orders imported will use outlet's invoice email address when true, else use invoice address from Sellar
    */
    public useOutletInvoiceEmailAddress: boolean = false;
    public readOnly: SellarSettingsReadOnly = new SellarSettingsReadOnly();
    public shippingMappings: (SellarShippingMapping)[] = [];
    public stockUpdateStockLocations: (SellarStockUpdateLocation)[] = [];
    private _sellarCreditGroupId: Guid | null = null;
    /**
    * The Group to which orders placed with the Sellar Credit option should be invoiced. If null then Sellar Credit orders will not be processed.
    */
    public get sellarCreditGroupId(): Guid | null { return this._sellarCreditGroupId }
    public set sellarCreditGroupId(value: Guid | null) {
      this._sellarCreditGroupId = value === undefined ? null : value;
    }
    private _sellarCreditChargeItemId: Guid | null = null;
    /**
    * The Item to use to hold the charge made by Sellar for Sellar Credit orders.
    */
    public get sellarCreditChargeItemId(): Guid | null { return this._sellarCreditChargeItemId }
    public set sellarCreditChargeItemId(value: Guid | null) {
      this._sellarCreditChargeItemId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.accountName = jsonSerializedObject.accountName;
          this.apiToken = jsonSerializedObject.apiToken;
          this.deliveryStockItemId = jsonSerializedObject.deliveryStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveryStockItemId);
          this.ignoreBeforeDate = jsonSerializedObject.ignoreBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.ignoreBeforeDate);
          this.runInBackground = jsonSerializedObject.runInBackground;
          this.useOutletInvoiceEmailAddress = jsonSerializedObject.useOutletInvoiceEmailAddress;
          this.readOnly = new SellarSettingsReadOnly(jsonSerializedObject.readOnly);
          this.shippingMappings = jsonSerializedObject.shippingMappings.map((v: any) => new SellarShippingMapping(v));
          this.stockUpdateStockLocations = jsonSerializedObject.stockUpdateStockLocations.map((v: any) => new SellarStockUpdateLocation(v));
          this.sellarCreditGroupId = jsonSerializedObject.sellarCreditGroupId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.sellarCreditGroupId);
          this.sellarCreditChargeItemId = jsonSerializedObject.sellarCreditChargeItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.sellarCreditChargeItemId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.accountName = this.accountName;
        toRet.apiToken = this.apiToken;
        toRet.deliveryStockItemId = this.deliveryStockItemId == null ? null : this.deliveryStockItemId.toString();
        toRet.ignoreBeforeDate = this.ignoreBeforeDate == null ? null : this.ignoreBeforeDate.toString();
        toRet.runInBackground = this.runInBackground;
        toRet.useOutletInvoiceEmailAddress = this.useOutletInvoiceEmailAddress;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.shippingMappings = this.shippingMappings.map(v => v.toJsonSerializedObject());
        toRet.stockUpdateStockLocations = this.stockUpdateStockLocations.map(v => v.toJsonSerializedObject());
        toRet.sellarCreditGroupId = this.sellarCreditGroupId == null ? null : this.sellarCreditGroupId.toString();
        toRet.sellarCreditChargeItemId = this.sellarCreditChargeItemId == null ? null : this.sellarCreditChargeItemId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "SellarSettings.Id cannot be empty"},
            {field: "deliveryStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "SellarSettings.DeliveryStockItemId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "shippingMappings", rule: (v: any) => !!v || "shippingMappings is required"},
            {field: "shippingMappings", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SellarShippingMapping[]).reduce((acc: Array<string>, curr: SellarShippingMapping) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "stockUpdateStockLocations", rule: (v: any) => !!v || "stockUpdateStockLocations is required"},
            {field: "stockUpdateStockLocations", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SellarStockUpdateLocation[]).reduce((acc: Array<string>, curr: SellarStockUpdateLocation) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "sellarCreditGroupId", rule: (v: any) => v == null || !v.isEmpty() || "SellarSettings.SellarCreditGroupId cannot be empty"},
            {field: "sellarCreditChargeItemId", rule: (v: any) => v == null || !v.isEmpty() || "SellarSettings.SellarCreditChargeItemId cannot be empty"},
    ];
}


export class SellarTenantSettingsId extends ModelObject<SellarTenantSettingsId> {

    public tenantId: Guid = Guid.createEmpty();
    public sellarSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.sellarSettingsId = Guid.parseOrEmpty(jsonSerializedObject.sellarSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantId = this.tenantId.toString();
        toRet.sellarSettingsId = this.sellarSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "SellarTenantSettingsId.TenantId cannot be empty"},
            {field: "sellarSettingsId", rule: (v: any) => !v.isEmpty() || "SellarTenantSettingsId.SellarSettingsId cannot be empty"},
    ];
}


export class SellarSettingsService extends ServiceBase {

  public async getAllSellarSettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SellarSettings)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.sellarSettings.map((v: any) => new SellarSettings(v));;
    return this.makeJsonRequest("sellarSettings/v1/getAllSellarSettings", req, false, extractor, opts);
  };

  public async getSellarSettings(sellarSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SellarSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    sellarSettingsId:sellarSettingsId.toString(),
    };
    const extractor = (response:any) => new SellarSettings(response.sellarSettings);;
    return this.makeJsonRequest("sellarSettings/v1/getSellarSettings", req, false, extractor, opts);
  };

  public async saveSellarSettings(sellarSettings: SellarSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    sellarSettings:sellarSettings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("sellarSettings/v1/saveSellarSettings", req, true, extractor, opts);
  };

  public async hardDeleteSellarSettings(sellarSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    sellarSettingsId:sellarSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("sellarSettings/v1/hardDeleteSellarSettings", req, false, extractor, opts);
  };
}

export const sellarSettingsService = new SellarSettingsService();